import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import useAuthStore from '../../store/useAuthStore';
import './LandingScreen.css';

const LandingScreen = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { user, isLoading, fetchUserProfile, token } = useAuthStore();
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollAreaRef = useRef(null);

  useEffect(() => {
    // Ensure we have the latest user profile
    if (auth.isAuthenticated && !user && !isLoading) {
      fetchUserProfile();
    }
  }, [auth.isAuthenticated, user, isLoading, fetchUserProfile]);

  useEffect(() => {
    // If user has already accepted TOS, redirect to dashboard
    if (user && user.tosAccepted) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Handle scroll event to detect when user reaches the bottom
  const handleScroll = (e) => {
    const element = e.target;
    const bottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 5;

    if (bottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcceptTos = async () => {
    if (!hasAccepted) {
      setError('Debes aceptar los términos y condiciones para continuar');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      const userId = user?.id || auth.user?.profile?.sub;

      if (!userId) {
        throw new Error('No se pudo obtener el ID del usuario');
      }

      // Call the API to accept TOS
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(
        `${apiUrl}/users/${userId}/accept-tos`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al aceptar los términos y condiciones');
      }

      // Update the local user state
      await fetchUserProfile();

      // Force a full page reload to re-evaluate user state and avoid routing conflicts
      window.location.href = '/';
    } catch (err) {
      console.error('Error accepting TOS:', err);
      setError(err.message || 'Error al aceptar los términos y condiciones. Por favor, intenta de nuevo.');
    } finally {
      setAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="landing-screen">
        <div className="landing-container">
          <div className="loading-spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-screen">
      <div className="landing-container">
        <div className="landing-header">
          <h1>Bienvenido a Dentaris</h1>
          <p className="landing-subtitle">Sistema de Gestión Dental</p>
        </div>

        <div className="tos-content">
          <h2>Términos y Condiciones de Uso</h2>

          <div
            className="tos-scroll-area"
            ref={scrollAreaRef}
            onScroll={handleScroll}
          >
            <p>Última actualización: 21 de octubre de 2025</p>

            <h3>1. Aceptación de los Términos</h3>
            <p>El presente documento establece los términos y condiciones (“Términos”) bajo los cuales usted (“Usuario”) puede acceder y utilizar esta plataforma web (“Servicio”), propiedad de la empresa responsable (“Proveedor”). Al utilizar el Servicio, usted acepta plenamente estos Términos. Si no está de acuerdo con ellos, no deberá utilizar el Servicio.</p>

            <h3>2. Descripción del Servicio</h3>
            <p>El Servicio es una aplicación web de tipo Software como Servicio (SaaS) que permite el uso de herramientas digitales para gestión administrativa y operativa. El Proveedor se compromete a mantener la seguridad, integridad y disponibilidad razonable de la información del Usuario, utilizando prácticas de protección de datos alineadas con la legislación mexicana vigente.</p>

            <h3>3. Limitaciones de Uso y Advertencia Sanitaria</h3>
            <p>El Usuario reconoce expresamente que esta plataforma <strong>no cuenta actualmente con certificación ante COFEPRIS ni cumple con la Norma Oficial Mexicana NOM-024-SSA3-2012</strong>. Por lo tanto, <strong>no debe ser utilizada para el registro, diagnóstico o tratamiento de pacientes reales ni para fines médicos oficiales</strong>. El uso del Servicio es bajo su exclusiva responsabilidad.</p>
            <p>El Proveedor no asume ninguna responsabilidad derivada del uso indebido del Servicio en contextos clínicos, médicos o regulatorios que requieran certificación sanitaria.</p>

            <h3>4. Responsabilidad del Usuario</h3>
            <p>El Usuario se compromete a utilizar el Servicio conforme a la legislación aplicable en los Estados Unidos Mexicanos y a no emplearlo para actividades ilícitas, fraudulentas o que vulneren derechos de terceros. El Usuario es responsable de la veracidad y legalidad de la información que ingrese o procese mediante el Servicio.</p>

            <h3>5. Protección de Datos Personales</h3>
            <p>El Proveedor actúa como responsable del tratamiento de los datos personales recabados mediante el Servicio, conforme a lo dispuesto por la <strong>Ley Federal de Protección de Datos Personales en Posesión de los Particulares</strong> y su Reglamento. Los datos se almacenan y procesan de forma segura y únicamente con los fines descritos en el Aviso de Privacidad correspondiente.</p>

            <h3>6. Disponibilidad y Mantenimiento</h3>
            <p>El Proveedor realizará esfuerzos razonables para asegurar la disponibilidad continua del Servicio, sin embargo, no garantiza que esté libre de interrupciones, errores o fallos técnicos. El Proveedor podrá suspender temporalmente el acceso al Servicio para mantenimiento o actualización, notificando al Usuario cuando sea posible.</p>

            <h3>7. Pagos y No Reembolsos</h3>
            <p>El Servicio se ofrece bajo un esquema de suscripción. Todos los pagos realizados son <strong>no reembolsables</strong>, independientemente del uso o acceso efectivo al Servicio durante el periodo contratado.</p>
            <p>El Usuario autoriza el cargo automático a su método de pago registrado, salvo cancelación expresa de la suscripción antes del siguiente ciclo de facturación.</p>

            <h3>8. Propiedad Intelectual</h3>
            <p>Todo el contenido, código fuente, marcas, logotipos, textos e interfaces del Servicio son propiedad exclusiva del Proveedor o de sus licenciantes. Queda estrictamente prohibida su reproducción, modificación o distribución sin autorización previa y por escrito.</p>

            <h3>9. Limitación de Responsabilidad</h3>
            <p>El Proveedor no será responsable de daños directos, indirectos, incidentales o consecuentes derivados del uso o imposibilidad de uso del Servicio. El Usuario entiende y acepta que el uso del Servicio se realiza <strong>bajo su propio riesgo</strong>.</p>

            <h3>10. Modificaciones a los Términos</h3>
            <p>El Proveedor podrá modificar estos Términos en cualquier momento. Las modificaciones entrarán en vigor al publicarse en el sitio web o aplicación. El uso continuado del Servicio implica la aceptación de dichas modificaciones.</p>

            <h3>11. Legislación y Jurisdicción Aplicable</h3>
            <p>Estos Términos se interpretarán conforme a las leyes aplicables en los Estados Unidos Mexicanos. Cualquier controversia derivada de su interpretación o cumplimiento se someterá a la jurisdicción de los tribunales competentes en la Ciudad de México, renunciando las partes a cualquier otro fuero que pudiera corresponderles.</p>

            <h3>12. Contacto</h3>
            <p>Para cualquier aclaración o ejercicio de derechos relacionados con estos Términos, el Usuario podrá comunicarse al correo electrónico oficial del Proveedor indicado en el sitio web.</p>

            <hr />
            <p>Al utilizar el Servicio, el Usuario declara haber leído y comprendido estos Términos y Condiciones, aceptando expresamente su contenido.</p>
          </div>

          <div className="tos-acceptance">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={hasAccepted}
                onChange={(e) => {
                  setHasAccepted(e.target.checked);
                  setError(null);
                }}
                disabled={accepting || !hasScrolledToBottom}
              />
              <span>
                He leído y acepto los términos y condiciones de uso de Dentaris
              </span>
            </label>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="tos-actions">
            <button
              className="btn-accept"
              onClick={handleAcceptTos}
              disabled={!hasAccepted || accepting}
            >
              {accepting ? 'Aceptando...' : 'Aceptar y Continuar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingScreen;
